// apps/web/src/pages/author/constraint-config/selectability-config.tsx
//
// Phase 9 Plan 08 Task 2 — Selectability constraint authoring sub-form
// (CSTR-01 5th-and-final constraint kind + CSTR-05 explanation surface).
//
// Composes the recursive PredicateNode editor (Task 1) with a ScrollArea
// viewport, the PredicateBoundsCounter aside the predicate header, recipeId
// Input, and the explanation Textarea. Structure mirrors the Phase 8 widget-
// config sub-forms (e.g. apps/web/src/pages/author/widget-config/option-set-
// config.tsx) — same defaultXConfig() factory + same per-field FormField
// pattern.
//
// UI-SPEC §"Selectability" copy table (lines 503-516):
//   Helper: "Defines when this option can be selected, using a structured rule."
//   Predicate editor heading: "This option is selectable when"
//   Predicate bounds counter: "DEPTH {d}/8 — BREADTH {b}/10 — NODES {n}/100"
//
// authr-06 hygiene: per-path useWatch only (handled by FormField + the inner
// PredicateNode + PredicateBoundsCounter components). No top-level useWatch.
// No formState reads.

import { useFormContext } from "react-hook-form";
import { Fieldset } from "@/components/ui/fieldset";
import { FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Textarea } from "@/components/ui/textarea";
import { PredicateBoundsCounter } from "./PredicateBoundsCounter.js";
import { PredicateNode } from "./PredicateNode.js";
import type { ConstraintConfigSubFormProps } from "./types.js";

// =============================================================================
// Default factory
// =============================================================================
//
// CSTR-01 v1 selectability default: single boolean leaf "batch-set-empty
// is-true". The explanation is "" (fails .min(1) — the user MUST supply text
// before save, matching the CSTR-05 contract). recipeId is "" — the user
// selects the target recipe via the recipe picker (Phase 9 Plan 12).
//
// Note: this factory returns a shape that DOES NOT parse cleanly through the
// Constraint Zod schema (explanation: "" + recipeId: "" both fail .min(1));
// that is intentional and matches the Phase 8 widget-config factory
// convention — RHF surfaces "Required" via FormMessage on the empty fields
// at edit time, and the save-time Pass 2A validator catches it as a final
// guard. Tests that exercise schema parity inject realistic values before
// safeParse.

export interface SelectabilityConfigShape {
  kind: "selectability";
  recipeId: string;
  predicate: {
    kind: "leaf";
    variable: "batch-set-empty";
    operator: "is-true";
  };
  explanation: string;
}

export function defaultSelectabilityConfig(): SelectabilityConfigShape {
  return {
    kind: "selectability",
    recipeId: "",
    predicate: {
      kind: "leaf",
      variable: "batch-set-empty",
      operator: "is-true",
    },
    explanation: "",
  };
}

// =============================================================================
// SelectabilityConfigSubForm
// =============================================================================

export function SelectabilityConfigSubForm({
  constraintId: _constraintId,
  basePath,
}: ConstraintConfigSubFormProps): React.ReactElement {
  const { control } = useFormContext();

  return (
    <div className="flex flex-col gap-4">
      <Fieldset legend="SELECTABILITY">
        <FormField
          control={control}
          name={`${basePath}.recipeId`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.recipeId`}>
              <FormLabel>Recipe id</FormLabel>
              <FormControl>
                <Input
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  placeholder="recipe this selectability rule applies to"
                  aria-describedby={`${basePath}-recipeId-helper`}
                />
              </FormControl>
              <p
                id={`${basePath}-recipeId-helper`}
                className="text-xs text-muted-foreground"
              >
                Recipe this selectability rule applies to.
              </p>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>

      <Fieldset legend="THIS OPTION IS SELECTABLE WHEN">
        <div className="flex items-center justify-between gap-2">
          <p className="text-xs text-muted-foreground">
            Defines when this option can be selected, using a structured rule.
          </p>
          <PredicateBoundsCounter predicatePath={`${basePath}.predicate`} />
        </div>
        <ScrollArea className="h-96 w-full rounded-md border border-input bg-background">
          <div className="p-2">
            <PredicateNode path={`${basePath}.predicate`} depth={0} />
          </div>
        </ScrollArea>
      </Fieldset>

      <Fieldset legend="EXPLANATION">
        <FormField
          control={control}
          name={`${basePath}.explanation`}
          render={({ field: f }) => (
            <FormItem data-field-path={`${basePath}.explanation`}>
              <FormLabel>Explanation</FormLabel>
              <FormControl>
                <Textarea
                  {...f}
                  value={(f.value as string | undefined) ?? ""}
                  rows={3}
                  maxLength={500}
                  placeholder="Plain-language description shown to users when this rule applies."
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </Fieldset>
    </div>
  );
}
SelectabilityConfigSubForm.displayName = "SelectabilityConfigSubForm";
